import { LogManager } from '@src/utility/Logger';
import { DEFAULT_SCENE_MANIPULATION_LIMIT } from '@src/utility/useFlagsCombination';
import { useEffect } from 'react';

import type { LayoutStrategy } from '@src/editor/models/layout/types';

const logger = LogManager.getLogger('hook:useLayoutStrategyConfig');

/** Narrow interface – any object that exposes `setPreviewLayoutConfig`. */
interface LayoutConfigurable {
  setPreviewLayoutConfig(config: {
    strategy?: LayoutStrategy;
    sceneCountThreshold?: number;
  }): void;
}

interface LayoutFlags {
  initialized: boolean;
  creatorStudioLayoutStrategy?: string;
  sceneManipulationLimit?: number;
}

export const isValidStrategy = (s: unknown): s is LayoutStrategy =>
  s === 'keyframe' ||
  s === 'split' ||
  s === 'hybrid' ||
  s === 'hybrid-adaptive';

/**
 * Synchronises LaunchDarkly layout-strategy flags to an editor store.
 *
 * Accepts a narrow `LayoutConfigurable` shape so it works with both
 * `EditorStore` and `ClipPreviewEditorStore` without importing concrete types.
 */
export function useLayoutStrategyConfig(
  editorStore: LayoutConfigurable | undefined | null,
  flags: LayoutFlags,
): void {
  useEffect(() => {
    if (!flags.initialized || !editorStore) return;

    const strategy = flags.creatorStudioLayoutStrategy;
    const validatedStrategy: LayoutStrategy = isValidStrategy(strategy)
      ? strategy
      : 'hybrid';
    const threshold =
      typeof flags.sceneManipulationLimit === 'number'
        ? flags.sceneManipulationLimit
        : DEFAULT_SCENE_MANIPULATION_LIMIT;

    logger.debug('Applying layout config', {
      rawStrategy: flags.creatorStudioLayoutStrategy,
      validatedStrategy,
      threshold,
    });

    editorStore.setPreviewLayoutConfig({
      strategy: validatedStrategy,
      sceneCountThreshold: threshold,
    });
  }, [editorStore, flags]);
}
